/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Add-a-card step. Opens the (decrypted) Stripe Checkout setup URL from
 * GET /storefront/card-checkout-url in a new window and polls get-cards until a new card appears.
 * Cards are kept per rental company: pass the car being booked (carId) and/or the company (ownerId) so the
 * card lands in — and is looked for in — that company's card set.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Loader2, CreditCard, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Props {
  onClose: () => void;
  onDone: () => void; // card added (or user proceeds without)
  /** The rental company whose card set receives the card. */
  ownerId?: string;
  /** The car being booked — the server takes its owner as the card set. */
  carId?: string;
}

export default function CardModal({ onClose, onDone, ownerId, carId }: Props) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [added, setAdded] = useState(false);
  // True when the card form is the booking PLATFORM's hosted page (e.g. HQ): the card lands on the platform's
  // gateway, not ours — so we must NOT poll our DB (it will never show the card) and just let the user proceed.
  const [external, setExternal] = useState(false);
  const userId = api.getStoredUser()?.id || '';
  const pollRef = useRef<number | null>(null);
  const scope = { ownerId, carId };
  // Cards already on file in this set when the modal opened — "added" means one MORE than that.
  const baselineRef = useRef<number | null>(null);
  const cardAdded = (count: number) => count > (baselineRef.current ?? 0);

  useEffect(() => {
    if (userId) api.getCards(userId, scope).then((c) => { if (baselineRef.current === null) baselineRef.current = c.length; });
    api.getCardCheckoutUrl(scope).then(({ url: u, external: ext }) => {
      setUrl(u); setExternal(ext); setLoading(false);
      if (u) window.open(u, '_blank', 'noopener');
    });
    // Poll for a card appearing on file — only when WE collect (our Stripe). Skipped for the platform's page.
    pollRef.current = window.setInterval(async () => {
      if (!userId || baselineRef.current === null) return;
      const cards = await api.getCards(userId, scope);
      if (cardAdded(cards.length)) {
        setAdded(true);
        if (pollRef.current) window.clearInterval(pollRef.current);
        setTimeout(onDone, 900);
      }
    }, 4000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop polling our DB once we know the card is being added on the platform's gateway.
  useEffect(() => {
    if (external && pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, [external]);

  const checkNow = async () => {
    // Platform-hosted card: we can't see it in our DB → just proceed.
    if (external) { setAdded(true); setTimeout(onDone, 700); return; }
    setChecking(true);
    try {
      const cards = await api.getCards(userId, scope);
      if (cardAdded(cards.length)) { setAdded(true); setTimeout(onDone, 700); }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="dialog-card w-full max-w-sm p-6 relative text-center" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"><X size={20} /></button>

        {added ? (
          <>
            <CheckCircle2 className="mx-auto text-green-500" size={44} />
            <h2 className="mt-3 text-lg font-bold text-gray-900">{t('card.added', 'Card added!')}</h2>
          </>
        ) : (
          <>
            <CreditCard className="mx-auto text-gray-700" size={40} />
            <h2 className="mt-3 text-lg font-bold text-gray-900">{t('card.title', 'Add a payment card')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('card.subtitle', 'A secure card form opened in a new window. Add your card, then come back here.')}</p>

            {loading ? (
              <Loader2 className="mx-auto mt-4 h-6 w-6 animate-spin text-gray-400" />
            ) : (
              <>
                {url && (
                  <a href={url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-blue-600 text-sm hover:underline">
                    {t('card.openAgain', 'Open card form again')} <ExternalLink size={14} />
                  </a>
                )}
                <button onClick={checkNow} disabled={checking} className="btn-primary w-full mt-4 py-2.5">
                  {checking ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : t('card.iveAdded', "I've added my card")}
                </button>
                <button onClick={onDone} className="w-full mt-2 text-sm text-gray-500 hover:underline">
                  {t('card.later', 'Add later')}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
